import { useState, useEffect, useRef } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DashboardPage } from '../../components/PremiumDesign';
import { ChatSidebar, ChatArea, EmailChatArea } from '../components';
import {
  useInfiniteWhatsAppConversations,
  useCustomWhatsAppMessages,
  useWhatsAppConversationDetail,
  useSendWhatsAppMessage,
  useReactToWhatsAppMessage,
  useInfiniteInstagramConversations,
  useCustomInstagramMessages,
  useSendInstagramMessage,
  useReactToInstagramMessage,
  useInfiniteGmailConversations,
  useCustomGmailMessages,
  useSendGmailMessage,
  useGmailSyncStatus,
  useSyncGmail,
  useSelectedPlatform,
  useGmailCategories,
} from '../index';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';
import { useDelayedSkeleton } from '@/common/hooks';
import { flattenImageOntoWhite } from '../utils/flattenImageOntoWhite';
import heic2any from 'heic2any';

import {
  formatMediaUrl,
  normalizeDirection,
  normalizeStatus,
} from '../utils/groupMessages';
import { formatChatDateLabel, messageTimeSource } from '../utils/chatDateLabel';
import { decodeHtmlEntities } from '@/common/utils';

function formatTime(dateString) {
  if (!dateString) return '';
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return dateString;
  }
}

function formatMessageTime(dateString) {
  if (!dateString) return '';
  try {
    return format(new Date(dateString), 'hh:mm a');
  } catch {
    return dateString;
  }
}

const SPLIT_INBOX_MIN = 720;

function estimateInboxSplit() {
  if (typeof window === 'undefined') return false;
  const sidebar = window.innerWidth >= 1024 ? 260 : 0;
  const pad = window.innerWidth >= 1024 ? 32 : 24;
  return window.innerWidth - sidebar - pad >= SPLIT_INBOX_MIN;
}

export function WhatsAppChatPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPlatform, setSelectedPlatform] = useSelectedPlatform();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname === '/email-inbox') {
      if (selectedPlatform !== 'email') {
        setSelectedPlatform('email');
      }
    } else if (location.pathname === '/whatsapp-chat') {
      if (selectedPlatform === 'email') {
        setSelectedPlatform('whatsapp');
      }
    }
  }, [location.pathname, selectedPlatform, setSelectedPlatform]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [preferList, setPreferList] = useState(true);
  const [splitPanes, setSplitPanes] = useState(estimateInboxSplit);
  const inboxRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [windowFilter, setWindowFilter] = useState(''); // '' | 'active' | 'inactive'
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('primary');
  const [activeFolder, setActiveFolder] = useState('inbox');
  const [activeLabel, setActiveLabel] = useState(null);
  const [reactions, setReactions] = useState({});

  const reactToWhatsAppMessage = useReactToWhatsAppMessage();
  const reactToInstagramMessage = useReactToInstagramMessage();

  const reactToMessage =
    selectedPlatform === 'instagram'
      ? reactToInstagramMessage
      : reactToWhatsAppMessage;

  const deepLinkChannel = searchParams.get('channel');
  const deepLinkConversationId = searchParams.get('conversation_id');

  useEffect(() => {
    const el = inboxRef.current;
    if (!el) return undefined;
    const measure = () => {
      setSplitPanes(el.clientWidth >= SPLIT_INBOX_MIN);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedPlatform]);

  useEffect(() => {
    setActiveConversation(null);
    setPreferList(true);
  }, [
    selectedPlatform,
    activeCategory,
    activeFolder,
    activeLabel,
    windowFilter,
    unreadOnly,
  ]);

  useEffect(() => {
    if (!deepLinkConversationId && !deepLinkChannel) return;
    if (
      (deepLinkChannel === 'whatsapp' || deepLinkChannel === 'instagram') &&
      selectedPlatform !== deepLinkChannel
    ) {
      setSelectedPlatform(deepLinkChannel);
      return;
    }
    if (deepLinkConversationId) {
      setActiveConversation({ id: String(deepLinkConversationId) });
      setPreferList(false);
    }
    setSearchParams({}, { replace: true });
  }, [
    deepLinkChannel,
    deepLinkConversationId,
    selectedPlatform,
    setSelectedPlatform,
    setSearchParams,
  ]);

  // Debounce search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleReactToMessage = (messageId, emoji) => {
    if (!currentCompanyId) return;

    // Optimistic toggle
    setReactions((prev) => {
      const current = prev[messageId] || [];
      const updated = current.includes(emoji)
        ? current.filter((e) => e !== emoji)
        : [...current, emoji];
      return {
        ...prev,
        [messageId]: updated,
      };
    });

    reactToMessage.mutate(
      {
        companyId: currentCompanyId,
        messageId: messageId,
        emoji: emoji,
      },
      {
        onSuccess: (data) => {
          setReactions((prev) => ({
            ...prev,
            [messageId]: data.reaction || [],
          }));
        },
        onError: () => {
          toast.error('Failed to update reaction');
          refetch();
        },
      },
    );
  };

  // Get company ID from local/session storage
  let currentCompanyId =
    localStorage.getItem('company_id') || sessionStorage.getItem('company_id');
  if (!currentCompanyId) {
    try {
      const userStr =
        localStorage.getItem('user') || sessionStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        currentCompanyId = user?.company_id || user?.company?.id || user?.id;
      }
    } catch {
      // ignore
    }
  }

  const queryParams = {
    is_window_active: windowFilter || undefined,
    unread_only: unreadOnly ? 'true' : undefined,
    search: debouncedSearchQuery || undefined,
    category:
      !activeLabel && activeFolder === 'inbox'
        ? activeCategory || undefined
        : undefined,
    folder:
      activeFolder !== 'inbox' || activeLabel
        ? activeFolder || undefined
        : undefined,
    label: activeLabel || undefined,
  };

  const isWhatsAppActive = selectedPlatform === 'whatsapp' || !selectedPlatform;
  const isInstagramActive = selectedPlatform === 'instagram';
  const isEmailActive = selectedPlatform === 'email';

  const whatsappConversations = useInfiniteWhatsAppConversations(
    currentCompanyId,
    queryParams,
    isWhatsAppActive,
  );
  const instagramConversations = useInfiniteInstagramConversations(
    currentCompanyId,
    queryParams,
    isInstagramActive,
  );
  const gmailConversations = useInfiniteGmailConversations(
    currentCompanyId,
    queryParams,
    isEmailActive,
  );

  const gmailSyncStatusQuery = useGmailSyncStatus(
    currentCompanyId,
    isEmailActive,
  );

  const gmailCategoriesQuery = useGmailCategories(
    currentCompanyId,
    isEmailActive,
  );

  const startGmailSyncMutation = useSyncGmail();

  const handleStartSync = (mode = 'all') => {
    startGmailSyncMutation.mutate({ companyId: currentCompanyId, mode });
  };

  const activeConversationsQuery = isEmailActive
    ? gmailConversations
    : isInstagramActive
      ? instagramConversations
      : whatsappConversations;

  const {
    data: conversationsData,
    isLoading: isApiLoadingConversations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = activeConversationsQuery;

  const isLoadingConversations = useDelayedSkeleton(
    isApiLoadingConversations,
    0,
  );

  const whatsappMessages = useCustomWhatsAppMessages(
    currentCompanyId,
    isWhatsAppActive ? activeConversation?.id : null,
    {},
    isWhatsAppActive,
  );
  const instagramMessages = useCustomInstagramMessages(
    currentCompanyId,
    isInstagramActive ? activeConversation?.id : null,
    {},
    isInstagramActive,
  );
  const gmailMessages = useCustomGmailMessages(
    currentCompanyId,
    isEmailActive ? activeConversation : null,
    isEmailActive,
  );

  const activeMessagesQuery = isEmailActive
    ? gmailMessages
    : isInstagramActive
      ? instagramMessages
      : whatsappMessages;

  const {
    messages: rawMessages,
    isLoadingInitial: isApiLoadingMessages,
    isFetchingOlder,
    fetchOlderMessages,
    fetchNewerMessages: refetch,
    hasOlder,
  } = activeMessagesQuery;

  const isLoadingMessages = isApiLoadingMessages;

  // Fetch full conversation detail to get live window_active status (WhatsApp only)
  const { data: conversationDetail, refetch: refetchConversationDetail } =
    useWhatsAppConversationDetail(
      currentCompanyId,
      isWhatsAppActive ? activeConversation?.id : null,
      isWhatsAppActive,
    );

  const isWindowActive = isEmailActive
    ? true
    : conversationDetail
      ? conversationDetail.is_window_active
      : (activeConversation?.isWindowActive ?? true);

  const windowExpiresAt = isEmailActive
    ? null
    : (conversationDetail?.window_expires_at ?? null);

  // Group messages by date for UI
  useEffect(() => {
    if (!rawMessages || rawMessages.length === 0) {
      setMessages((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    const sortedMessages = [...rawMessages].sort((a, b) => {
      const tA = new Date(messageTimeSource(a) || a.date || 0).getTime();
      const tB = new Date(messageTimeSource(b) || b.date || 0).getTime();
      return tA - tB; // Ascending: oldest first
    });

    const grouped = {};
    sortedMessages.forEach((msg) => {
      const dateKey = formatChatDateLabel(messageTimeSource(msg) || msg.date);
      if (!grouped[dateKey]) grouped[dateKey] = [];
      grouped[dateKey].push(msg);
    });

    const uniqueDatesAsc = [
      ...new Set(
        sortedMessages.map((msg) =>
          formatChatDateLabel(messageTimeSource(msg) || msg.date),
        ),
      ),
    ];

    const finalMappedGroups = uniqueDatesAsc.map((dateKey) => {
      const msgs = grouped[dateKey].map((msg) => {
        const firstAttachment = msg.attachments?.[0];
        const templateHeader = msg.template?.header;
        const rawUrl =
          firstAttachment?.file_url ||
          firstAttachment?.file ||
          templateHeader?.url ||
          templateHeader?.file ||
          msg.template?.media_url ||
          msg.template?.image_url ||
          msg.file_url ||
          msg.file ||
          msg.mediaUrl;
        const mediaUrl = formatMediaUrl(rawUrl);

        const messageType = msg.message_type;
        const headerFormat = (templateHeader?.format || '').toUpperCase();
        const isImage =
          headerFormat === 'IMAGE' ||
          (rawUrl &&
            typeof rawUrl === 'string' &&
            /\.(jpg|jpeg|png|webp|gif|svg)(\?.*)?$/i.test(rawUrl));
        const isVideo =
          headerFormat === 'VIDEO' ||
          (rawUrl &&
            typeof rawUrl === 'string' &&
            /\.(mp4|mov|webm)(\?.*)?$/i.test(rawUrl));
        const isDoc =
          headerFormat === 'DOCUMENT' ||
          (rawUrl &&
            typeof rawUrl === 'string' &&
            /\.(pdf|doc|docx|xls|xlsx|csv)(\?.*)?$/i.test(rawUrl));

        const mimeType =
          firstAttachment?.mime_type ||
          templateHeader?.mime_type ||
          (firstAttachment?.file_type
            ? firstAttachment.file_type.includes('/')
              ? firstAttachment.file_type
              : `${firstAttachment.file_type}/*`
            : null) ||
          (isImage
            ? 'image/png'
            : isVideo
              ? 'video/mp4'
              : isDoc
                ? 'application/pdf'
                : messageType === 'audio'
                  ? 'audio/*'
                  : messageType === 'image'
                    ? 'image/*'
                    : messageType === 'video'
                      ? 'video/*'
                      : null);

        const buttons =
          msg.template?.buttons ||
          msg.template?.components?.find((c) => c.type === 'BUTTONS')
            ?.buttons ||
          msg.buttons ||
          [];

        return {
          id: msg.id?.toString(),
          content: msg.content || msg.body || msg.text || '',
          direction: normalizeDirection(msg),
          time: formatMessageTime(messageTimeSource(msg) || msg.date),
          status: normalizeStatus(msg, normalizeDirection(msg)),
          messageType: messageType,
          mediaUrl: mediaUrl,
          mimeType: mimeType,
          fileName:
            firstAttachment?.file_name ||
            firstAttachment?.filename ||
            firstAttachment?.name,
          fileSize:
            firstAttachment?.file_size ||
            firstAttachment?.filesize ||
            firstAttachment?.size,
          timestamp: messageTimeSource(msg) || msg.date,
          isDeleted: msg.is_deleted,
          reaction:
            reactions[msg.id?.toString()] !== undefined
              ? reactions[msg.id?.toString()]
              : msg.reaction || [],
          buttons: buttons,
          // Carousel template data
          template: msg.template || null,
          attachments: msg.attachments || [],
          is_html:
            msg.is_html ||
            Boolean(
              (msg.content || msg.body) &&
              (/<[a-z][\s\S]*>/i.test(msg.content || msg.body) ||
                (msg.content || msg.body).includes('<p>') ||
                (msg.content || msg.body).includes('<p ') ||
                (msg.content || msg.body).includes('<br') ||
                (msg.content || msg.body).includes('<div>') ||
                (msg.content || msg.body).includes('<div ') ||
                (msg.content || msg.body).includes('</span>') ||
                (msg.content || msg.body).includes('class=')),
            ),
          from_name: msg.from_name || msg.from, // Important for emails
          from_email: msg.from_email,
          to_email: msg.to_email || msg.to,
          subject: msg.subject,
        };
      });

      return { date: dateKey, messages: msgs };
    });

    setMessages(finalMappedGroups);
  }, [rawMessages, reactions]);

  // Map API data to component props — flatten all infinite pages
  const conversationsList = (conversationsData?.pages || [])
    .flatMap((page) => page?.results || [])
    .map((conv) => {
      const rawName = conv.name?.trim() || conv.customer_name?.trim();
      const rawUsername = conv.username?.trim();

      let name = rawName;
      if (!name) {
        if (rawUsername) {
          name = `@${rawUsername}`;
        } else if (conv.customer_phone) {
          name = conv.customer_phone;
        } else if (conv.instagram_scoped_user_id) {
          name = 'Instagram User';
        } else {
          name = 'Unknown';
        }
      }

      const phoneOrUsername =
        rawUsername && rawName
          ? `@${rawUsername}`
          : conv.customer_phone ||
            (rawUsername ? `@${rawUsername}` : '') ||
            (conv.instagram_scoped_user_id
              ? `ID: ${conv.instagram_scoped_user_id}`
              : '');

      const lastMessageText =
        conv.last_message_preview ||
        conv.last_message?.body ||
        (typeof conv.last_message === 'string' ? conv.last_message : null) ||
        'Tap to view messages...';

      return {
        ...conv,
        id: conv.id?.toString(),
        contactName: name,
        contactPhone: phoneOrUsername || conv.from_email || '',
        recipientId: conv.instagram_scoped_user_id || conv.customer_phone,
        lastMessage: lastMessageText,
        time: formatTime(
          conv.last_message_at ||
            conv.last_inbound_message_at ||
            conv.last_message?.timestamp ||
            conv.timestamp ||
            conv.date ||
            conv.created_at,
        ),
        unreadCount: conv.unread_count || 0,
        avatarUrl: conv.profile_picture_url || null,
        isWindowActive: Boolean(conv.is_window_active),
        chatMode: conv.chat_mode || 'bot',
        username: conv.username,
        raw: conv,
      };
    });

  const filteredConversationsList = conversationsList.filter((conv) => {
    if (windowFilter === 'active') return conv.isWindowActive;
    if (windowFilter === 'inactive') return !conv.isWindowActive;
    return true;
  });

  // markAsRead unused, handled inside hook

  useEffect(() => {
    if (!activeConversation?.id || activeConversation.contactName) return;
    const match = conversationsList.find(
      (conv) => conv.id === String(activeConversation.id),
    );
    if (match) setActiveConversation(match);
  }, [conversationsList, activeConversation]);

  const handleSelectConversation = (conv) => {
    // Always open the chat – do not block on window status
    setActiveConversation(conv);
    setPreferList(false);
  };

  const sendWhatsAppMessage = useSendWhatsAppMessage();
  const sendInstagramMessage = useSendInstagramMessage();
  const sendGmailMessage = useSendGmailMessage();

  const sendMessage =
    selectedPlatform === 'email'
      ? sendGmailMessage
      : selectedPlatform === 'instagram'
        ? sendInstagramMessage
        : sendWhatsAppMessage;

  const handleSendMessage = async (
    text,
    file,
    customSubject,
    targetTo,
    cc,
    bcc,
  ) => {
    if (!text?.trim() && !file) return;
    if (!activeConversation || !currentCompanyId) return;

    // Guard: prevent sending if window is expired (only for WhatsApp)
    if (selectedPlatform === 'whatsapp' && !isWindowActive) {
      toast.error(
        'This WhatsApp conversation window is expired. You cannot send a new message.',
      );
      return;
    }

    if (file) {
      const fileExt = file.name
        .substring(file.name.lastIndexOf('.'))
        .toLowerCase();
      const isVideoFile =
        file.type?.startsWith('video/') ||
        ['.mp4', '.3gp', '.mov', '.avi', '.mkv', '.webm'].includes(fileExt);

      if (isVideoFile && file.size > 16 * 1024 * 1024) {
        toast.error('Video size exceeds 16MB limit');
        return;
      }
    }

    let mediaFile = file;
    if (
      file?.name?.toLowerCase().endsWith('.heic') ||
      file?.name?.toLowerCase().endsWith('.heif')
    ) {
      const loadingToastId = toast.loading('Converting HEIC image...');
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.8,
        });
        const blob = Array.isArray(convertedBlob)
          ? convertedBlob[0]
          : convertedBlob;
        mediaFile = new File(
          [blob],
          file.name.replace(/\.hei(c|f)$/i, '.jpeg'),
          { type: 'image/jpeg' },
        );
        toast.dismiss(loadingToastId);
      } catch {
        toast.dismiss(loadingToastId);
        toast.error('Failed to convert HEIC image');
        return;
      }
    }

    // Flatten transparent images onto white before preview/upload so
    // WhatsApp/API processing cannot turn transparency into black.
    if (mediaFile?.type?.startsWith('image/')) {
      mediaFile = await flattenImageOntoWhite(mediaFile);
    }

    const isAudio =
      mediaFile?.type?.startsWith('audio/') ||
      mediaFile?.name?.endsWith('.ogg');
    const isImage = mediaFile?.type?.startsWith('image/');
    const isVideo = mediaFile?.type?.startsWith('video/');

    let messageType = 'text';
    if (isAudio) messageType = 'audio';
    else if (isImage) messageType = 'image';
    else if (isVideo) messageType = 'video';
    else if (mediaFile) messageType = 'document';

    if (selectedPlatform === 'instagram' && messageType === 'document') {
      toast.error(
        'Instagram Direct Messages only support image, video, and audio files. Documents cannot be sent.',
      );
      return;
    }

    const newMessageId = Date.now().toString();
    const newMessage = {
      id: newMessageId,
      content: text || '',
      direction: 'outbound',
      time: 'Just now',
      status: 'sending',
      messageType: messageType,
      mediaUrl: mediaFile ? URL.createObjectURL(mediaFile) : null,
      fileName: mediaFile ? mediaFile.name : null,
      fileSize: mediaFile ? mediaFile.size : null,
      mimeType: mediaFile ? mediaFile.type : null,
      is_html: selectedPlatform === 'email',
    };

    // Optimistic UI update
    setMessages((prevGroups) => {
      const todayStr = 'Today';
      if (prevGroups.length === 0) {
        return [{ date: todayStr, messages: [newMessage] }];
      }
      const lastGroup = prevGroups[prevGroups.length - 1];
      if (lastGroup.date === todayStr) {
        return [
          ...prevGroups.slice(0, -1),
          { ...lastGroup, messages: [...lastGroup.messages, newMessage] },
        ];
      }
      return [...prevGroups, { date: todayStr, messages: [newMessage] }];
    });

    let uploadToastId = null;
    if (mediaFile) {
      uploadToastId = toast.loading('Sending media...');
    }

    // Composer stores < and > as entities so the web bubble can render
    // formatting tags safely. WhatsApp and Instagram show those entities
    // as literal text, so the outbound body is decoded here.
    const outboundText = text ? decodeHtmlEntities(text) : text;

    // Build the payload
    let payload;
    if (selectedPlatform === 'email') {
      const isForward = customSubject?.toLowerCase().startsWith('fwd:');
      payload = {
        to:
          targetTo ||
          activeConversation.from_email ||
          activeConversation.raw?.from_email ||
          activeConversation.contactPhone ||
          activeConversation.name,
        cc: cc || undefined,
        bcc: bcc || undefined,
        subject:
          customSubject ||
          (activeConversation.subject
            ? `Re: ${activeConversation.subject}`
            : 'Reply from Aexon'),
        body: text,
        thread_id: isForward
          ? undefined
          : activeConversation.thread_id || activeConversation.raw?.thread_id,
      };
    } else if (selectedPlatform === 'instagram') {
      const recipient =
        activeConversation.recipientId ||
        activeConversation.raw?.instagram_scoped_user_id ||
        activeConversation.contactPhone;
      if (mediaFile) {
        payload = new FormData();
        payload.append('conversation_id', activeConversation.id);
        if (recipient) {
          payload.append('recipient_id', recipient);
          payload.append('instagram_scoped_user_id', recipient);
        }
        if (outboundText) payload.append('text', outboundText);
        payload.append('media_file', mediaFile);
        payload.append('message_type', messageType);
      } else {
        payload = {
          conversation_id: activeConversation.id,
          recipient_id: recipient,
          instagram_scoped_user_id: recipient,
          text: outboundText,
        };
      }
    } else {
      if (mediaFile) {
        payload = new FormData();
        payload.append('to_phone', activeConversation.contactPhone);
        if (outboundText) {
          payload.append('text', outboundText);
        }
        payload.append('media_file', mediaFile);
        payload.append('message_type', messageType);
      } else {
        payload = {
          to_phone: activeConversation.contactPhone,
          text: outboundText,
        };
      }
    }

    // Call the send message API
    sendMessage.mutate(
      {
        companyId: currentCompanyId,
        payload: payload,
      },
      {
        onSuccess: () => {
          if (uploadToastId) toast.dismiss(uploadToastId);
          refetch(); // Fetch the real message with ID and status to update optimism
        },
        onError: () => {
          if (uploadToastId) {
            toast.dismiss(uploadToastId);
            toast.error('Failed to send media');
          }
          // Mark optimistic message as failed
          setMessages((prevGroups) => {
            return prevGroups.map((group) => ({
              ...group,
              messages: group.messages.map((m) =>
                m.id === newMessageId ? { ...m, status: 'failed' } : m,
              ),
            }));
          });
        },
      },
    );
  };

  return (
    <DashboardPage className="!p-0 !bg-transparent !overflow-hidden">
      {selectedPlatform === 'email' ? (
        <div className="flex-1 min-h-0 w-full h-full overflow-hidden">
          <EmailChatArea
            conversations={filteredConversationsList}
            isLoadingConversations={isLoadingConversations}
            activeConversation={activeConversation}
            companyId={currentCompanyId}
            onSelectConversation={(conv) => handleSelectConversation(conv)}
            messages={messages}
            isLoadingMessages={isLoadingMessages}
            onSendMessage={(text, file, subject, targetTo, cc, bcc) =>
              handleSendMessage(text, file, subject, targetTo, cc, bcc)
            }
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onRefresh={() => {
              activeConversationsQuery.refetch({
                refetchPage: (page, index) => index === 0,
              });
              if (activeConversation) {
                refetch();
                if (isWhatsAppActive && activeConversation?.id) {
                  refetchConversationDetail();
                }
              }
            }}
            onNextPage={() => {
              if (hasNextPage) {
                fetchNextPage();
              }
            }}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalApiCount={conversationsData?.pages?.[0]?.count}
            syncInfo={
              gmailSyncStatusQuery.data?.sync || gmailConversations.syncInfo
            }
            onStartSync={handleStartSync}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            activeFolder={activeFolder}
            onFolderChange={setActiveFolder}
            activeLabel={activeLabel}
            onLabelChange={setActiveLabel}
            categories={gmailCategoriesQuery.data || []}
          />
        </div>
      ) : (
        <div
          ref={inboxRef}
          className={`relative flex flex-1 min-h-0 min-w-0 w-full h-full overflow-hidden ${
            splitPanes ? 'gap-4' : ''
          }`}
        >
          {/* Conversation list. Hidden with visibility so scroll position survives pane switches. */}
          <div
            className={`min-h-0 min-w-0 bg-white rounded-[24px] overflow-hidden flex-col h-full ${
              splitPanes
                ? 'relative flex w-80 shrink-0'
                : `absolute inset-0 ${
                    !activeConversation || preferList
                      ? 'flex visible'
                      : 'invisible pointer-events-none'
                  }`
            }`}
          >
            <ChatSidebar
              conversations={filteredConversationsList}
              isLoading={isLoadingConversations}
              activeConversationId={activeConversation?.id}
              onSelectConversation={handleSelectConversation}
              windowFilter={windowFilter}
              setWindowFilter={setWindowFilter}
              unreadOnly={unreadOnly}
              setUnreadOnly={setUnreadOnly}
              fetchNextPage={fetchNextPage}
              hasNextPage={hasNextPage}
              isFetchingNextPage={isFetchingNextPage}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              platform={selectedPlatform}
            />
          </div>

          {/* Right Area (Active Chat for WhatsApp & Instagram) */}
          <div
            className={`min-w-0 min-h-0 h-full bg-white rounded-[24px] overflow-hidden flex-col ${
              splitPanes
                ? 'relative flex flex-1'
                : `absolute inset-0 ${
                    activeConversation && !preferList
                      ? 'flex visible'
                      : 'invisible pointer-events-none'
                  }`
            }`}
          >
            <ChatArea
              activeConversation={activeConversation}
              messages={messages}
              isLoading={isLoadingMessages}
              hasNextPage={hasOlder}
              isFetchingNextPage={isFetchingOlder}
              fetchNextPage={fetchOlderMessages}
              onRefresh={() => {
                activeConversationsQuery.refetch({
                  refetchPage: (page, index) => index === 0,
                });
                if (activeConversation) {
                  refetch();
                  if (isWhatsAppActive && activeConversation?.id) {
                    refetchConversationDetail();
                  }
                }
              }}
              isWindowActive={isWindowActive}
              windowExpiresAt={windowExpiresAt}
              onMobileBack={
                !splitPanes && activeConversation
                  ? () => setPreferList(true)
                  : undefined
              }
              onSendMessage={handleSendMessage}
              onReactToMessage={handleReactToMessage}
              platform={selectedPlatform}
            />
          </div>
        </div>
      )}
    </DashboardPage>
  );
}
